import React, { useEffect, useState } from "react";
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Spinner,
  Center,
  Text,
  Badge,
  useColorModeValue,
  Box,
  Heading,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import { BellIcon, WarningIcon, TimeIcon, InfoIcon } from "@chakra-ui/icons";
import axios from "axios";
import { decryptData, URL_WEB_BACKEND } from "CustomUtil";

export default function KenaikanPangkatTable() {
  const authdata = decryptData(localStorage.getItem("authinfosemakinpas"));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const textColorPrimary = useColorModeValue("secondaryGray.900", "white");
  const bgBox = useColorModeValue("white", "gray.800");
  const bgHead = useColorModeValue("orange.600", "orange.500");
  const bgAlert = useColorModeValue("orange.50", "orange.900");
  const hoverBg = useColorModeValue("orange.50", "orange.900");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${URL_WEB_BACKEND}/kepegawaians/daftarKenaikanPangkat`,
          {
            headers: { Authorization: "Bearer " + authdata },
          }
        );

        console.log("Response:", res.data);
        setData(res.data.data || []);
      } catch (err) {
        console.error("Gagal memuat data kenaikan pangkat:", err);
        console.error("Error detail:", err.response?.data);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [authdata]);

  // Fungsi untuk menentukan status badge
  const getStatusBadge = (status, sisaWaktu) => {
    if (status === 'overdue' || sisaWaktu?.includes('lewat')) {
      return { colorScheme: 'red', icon: WarningIcon, label: 'TERLAMBAT' };
    }
    return { colorScheme: 'yellow', icon: TimeIcon, label: 'SEGERA' };
  };

  if (loading)
    return (
      <Center h="300px">
        <Spinner size="xl" color="orange.500" />
      </Center>
    );

  return (
    <Box>
      {/* Alert Notifikasi */}
      {data.length > 0 && (
        <Alert
          status="warning"
          bg={bgAlert}
          borderLeft="4px"
          borderColor="orange.500"
          mb={4}
          rounded="md"
        >
          <AlertIcon as={BellIcon} color="orange.600" />
          <Box flex="1">
            <AlertTitle color="orange.800" fontWeight="bold">
              Pemberitahuan Kenaikan Pangkat
            </AlertTitle>
            <AlertDescription color="orange.700">
              Terdapat <strong>{data.length} petugas</strong> yang sudah
              mencapai masa kerja <strong>3 tahun 10 bulan atau lebih</strong>.
              <br />
              Segera siapkan berkas untuk kenaikan pangkat (maksimal 4 tahun dari TMT terakhir + penundaan hukdis jika ada)!
            </AlertDescription>
          </Box>
        </Alert>
      )}

      <Box p={6} bg={bgBox} rounded="xl" shadow="md">
        <HStack mb={4} justify="space-between">
          <Heading fontSize="xl" color={textColorPrimary}>
            Daftar Kenaikan Pangkat
          </Heading>
          {data.length > 0 && (
            <Badge colorScheme="orange" fontSize="md" px={3} py={1}>
              <Icon as={BellIcon} mr={1} />
              {data.length} Petugas
            </Badge>
          )}
        </HStack>

        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead bg={bgHead}>
              <Tr>
                <Th color="white">No</Th>
                <Th color="white">Nama</Th>
                <Th color="white">Username</Th>
                <Th color="white">Jabatan</Th>
                <Th color="white">Pangkat/Golongan</Th>
                <Th color="white">TMT Pangkat Terakhir</Th>
                <Th color="white">Umur Pangkat</Th>
                <Th color="white">Hukdis</Th>
                <Th color="white">Deadline Kenaikan</Th>
                <Th color="white">Sisa Waktu</Th>
                <Th color="white">Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.length > 0 ? (
                data.map((item, index) => {
                  const statusInfo = getStatusBadge(item.status_kenaikan, item.sisa_waktu_kenaikan);
                  return (
                    <Tr key={index} _hover={{ bg: hoverBg }}>
                      <Td fontWeight="bold">{index + 1}</Td>
                      <Td fontWeight="semibold">{item.fullname?.toUpperCase() || "-"}</Td>
                      <Td>{item.username || "-"}</Td>
                      <Td>{item.jabatan || "-"}</Td>
                      <Td fontSize="xs">{item.pangkat_golongan || "-"}</Td>
                      <Td fontWeight="medium">{item.tmt_pangkat_terakhir || "-"}</Td>
                      <Td>
                        <Badge colorScheme="blue" fontSize="xs">
                          {item.umur_jabatan || "-"}
                        </Badge>
                      </Td>
                      <Td>
                        {item.ada_hukdis ? (
                          <Tooltip
                            label={item.keterangan_hukdis}
                            placement="top"
                            hasArrow
                          >
                            <Badge colorScheme="red" fontSize="xs" cursor="help">
                              <Icon as={InfoIcon} mr={1} />
                              +{item.penundaan_bulan} bulan
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Badge colorScheme="green" fontSize="xs">
                            Tidak ada
                          </Badge>
                        )}
                      </Td>
                      <Td>
                        <Text color="orange.700" fontWeight="bold" fontSize="sm">
                          {item.kenaikan_pangkat_berikutnya || "-"}
                        </Text>
                        {item.ada_hukdis && (
                          <Text fontSize="xs" color="red.600" fontStyle="italic">
                            (+ {item.penundaan_bulan} bln hukdis)
                          </Text>
                        )}
                      </Td>
                      <Td>
                        <Text
                          fontSize="sm"
                          fontWeight="semibold"
                          color={statusInfo.colorScheme === 'red' ? 'red.600' : 'orange.600'}
                        >
                          {item.sisa_waktu_kenaikan || "-"}
                        </Text>
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={statusInfo.colorScheme}
                          fontSize="xs"
                          px={2}
                          py={1}
                        >
                          <Icon as={statusInfo.icon} mr={1} />
                          {statusInfo.label}
                        </Badge>
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={11} textAlign="center" py={8}>
                    <Icon as={BellIcon} boxSize={12} color="green.400" mb={2} />
                    <Text color="green.600" fontSize="lg" fontWeight="bold">
                      ✅ Tidak Ada Notifikasi
                    </Text>
                    <Text color="gray.500" fontSize="sm" mt={1}>
                      Semua petugas belum mencapai 3 tahun 10 bulan masa kerja
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
}